import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { isString } from 'lodash';
import { selectMainClient } from '~/reducers/mainClientSlice';
import { FeatureFlag } from '~/utils/feature-flags-utils';
import { useFeatureFlag } from '../useFeatureFlag';
import type {
    UseIntermodalFeatures,
    IntermodalExternalTaskTypes
} from './types';

export const useIntermodalFeatures = (): UseIntermodalFeatures => {
    const mainClient = useSelector(selectMainClient);
    const {
        enableLiveDispatch: isEnabledLiveDispatch,
        enableGapInRoute: isEnabledGapInRoute,
        enableResetTasks: isEnabledResetTasks,
        enableOperationalStops: isEnabledOperationalStops,
        enableEndOfShift: isEnabledEndOfShift,
        enableUnarriveStops: isEnabledUnarriveStops,
        enableRescheduleStops: isEnabledRescheduleStops,
        enableTaskEquipmentField = {},
        carKind = [],
        externalTaskTypes = {}
    } = mainClient?.intermodal || {};

    const {
        pickup = [],
        delivery = [],
        twoPart = [],
        operationalStop = []
    } = externalTaskTypes;

    const {
        equipmentId: isEnabledFieldEquipmentId,
        carKind: isEnabledFieldCarKind,
        emptyOrderNumber: isEnabledFieldEmptyOrder,
        reservation: isEnabledFieldReservation,
        chassisId: isEnabledFieldChassisId,
        externalTaskType: isEnabledFieldExternalTaskType,
        showToDriver: isEnabledFieldShowToDriver
    } = enableTaskEquipmentField;

    const isFeatureEnabledUnarriveStops = useFeatureFlag(
        FeatureFlag.UNARRIVE_STOPS
    );

    const enableUnarriveStops = Boolean(
        isEnabledUnarriveStops || isFeatureEnabledUnarriveStops
    );

    const getMappedExternalTaskTypes = useCallback(
        (type: unknown) => {
            const isValidKey = (
                key: unknown
            ): key is keyof IntermodalExternalTaskTypes => {
                return isString(key);
            };

            if (!isValidKey(type)) return [];

            return externalTaskTypes[type] || [];
        },
        [externalTaskTypes]
    );

    return {
        getMappedExternalTaskTypes,
        carKind,
        externalTaskTypes: { pickup, delivery, twoPart, operationalStop },
        enableLiveDispatch: Boolean(isEnabledLiveDispatch),
        enableGapInRoute: Boolean(isEnabledGapInRoute),
        enableResetTasks: Boolean(isEnabledResetTasks),
        enableOperationalStops: Boolean(isEnabledOperationalStops),
        enableEditReservation: Boolean(isEnabledFieldReservation),
        enableEditEquipmentId: Boolean(isEnabledFieldEquipmentId),
        enableEditExternalTaskType: Boolean(isEnabledFieldExternalTaskType),
        enableEditCarKind: Boolean(isEnabledFieldCarKind),
        enableEditChassisId: Boolean(isEnabledFieldChassisId),
        enableEditEmptyOrder: Boolean(isEnabledFieldEmptyOrder),
        enableEditShowToDriver: Boolean(isEnabledFieldShowToDriver),
        enableEndOfShift: Boolean(isEnabledEndOfShift),
        enableUnarriveStops,
        enableRescheduleStops: Boolean(isEnabledRescheduleStops)
    };
};
